import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, DollarSign, Home, TrendingUp, PieChart, Settings, Eye, Download, Save, User, Info, PlusCircle, ExternalLink, Building2, Lightbulb, ChevronDown, ChevronUp, PiggyBank, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import Disclaimer from '../components/Disclaimer';
import { validationRules } from '../utils/validation';
import { getTaxYearOptions, isValidTaxYear } from '../utils/taxYears';
import '../components/ValidatedInput.css';

const TAX_YEAR_STORAGE_KEY = 'wealthcore_taxForms_year';

const getInitialTaxYear = () => {
  try {
    const stored = sessionStorage.getItem(TAX_YEAR_STORAGE_KEY);
    if (stored) {
      const year = parseInt(stored, 10);
      if (isValidTaxYear(year)) return year;
    }
  } catch (e) { /* ignore */ }
  return new Date().getFullYear();
};

const TAB_IDS = ['overview', 'income-sources', 'filing-info', '1040', 'schedule-e', 'adjustments'];
const TAB_LABELS = { overview: 'Overview', 'income-sources': 'Income & Roth', 'filing-info': 'Filing Info', '1040': 'Form 1040', 'schedule-e': 'Schedule E', adjustments: 'Manual Adjustments' };

const TaxForms = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(() => (tabFromUrl && TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'overview'));
  const setActiveTab = useCallback((tabOrFn) => {
    setActiveTabState(prev => {
      const next = typeof tabOrFn === 'function' ? tabOrFn(prev) : tabOrFn;
      if (TAB_IDS.includes(next)) {
        setSearchParams(p => { const n = new URLSearchParams(p); n.set('tab', next); return n; }, { replace: true });
      }
      return next;
    });
  }, [setSearchParams]);
  useEffect(() => {
    if (tabFromUrl && TAB_IDS.includes(tabFromUrl)) setActiveTabState(tabFromUrl);
  }, [tabFromUrl]);
  const [taxYear, setTaxYear] = useState(getInitialTaxYear);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [rawData, setRawData] = useState({});
  const [manualAdjustments, setManualAdjustments] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'unsaved'
  const [deductionSuggestions, setDeductionSuggestions] = useState([]);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  const saveTimeoutRef = useRef(null);
  const [rothForm, setRothForm] = useState({
    withdrawalDate: '',
    amount: '',
    isQualified: false,
    notes: ''
  });
  const [rothSaving, setRothSaving] = useState(false);
  // Form hook for filing info
  const { register: registerFilingInfo, handleSubmit: handleSubmitFilingInfo, formState: { errors: filingInfoErrors }, watch: watchFilingInfo, setValue: setFilingInfoValue } = useForm({
    defaultValues: {
      filingStatus: 'married_filing_jointly',
      numberOfDependents: 0,
      dependentsUnder17: 0,
      estimatedAge59: false,
      foreignIncomeExclusion: false
    }
  });

  const watchedNumberOfDependents = watchFilingInfo('numberOfDependents');
  const watchedFilingStatus = watchFilingInfo('filingStatus');
  const watchedDependentsUnder17 = watchFilingInfo('dependentsUnder17');
  const watchedEstimatedAge59 = watchFilingInfo('estimatedAge59');
  const watchedForeignIncomeExclusion = watchFilingInfo('foreignIncomeExclusion');
  const filingInfoSaveRef = useRef(null);

  const [filingInfo, setFilingInfo] = useState({
    filingStatus: 'married_filing_jointly',
    numberOfDependents: 0,
    dependentsUnder17: 0,
    estimatedAge59: false,
    foreignIncomeExclusion: false
  });

  useEffect(() => {
    loadTaxData();
  }, [taxYear]);

  useEffect(() => {
    loadDeductionSuggestions();
  }, [taxYear]);

  const loadDeductionSuggestions = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`/api/tax-forms/deduction-suggestions?taxYear=${taxYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success && res.data?.data?.suggestions) {
        setDeductionSuggestions(res.data.data.suggestions);
      }
    } catch (e) {
      console.error('Failed to load deduction suggestions:', e);
    }
  };

  useEffect(() => {
    loadFilingInfo();
  }, [taxYear]);

  useEffect(() => {
    loadAdjustments();
  }, [taxYear]);

  // Auto-save manual adjustments after 2 seconds of no changes (live document)
  useEffect(() => {
    if (!isDirty || Object.keys(manualAdjustments).length === 0) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        const token = localStorage.getItem('accessToken');
        await axios.post('/api/tax-forms/save-adjustments', {
          taxYear,
          adjustments: manualAdjustments
        }, { headers: { Authorization: `Bearer ${token}` } });
        setIsDirty(false);
        setSaveStatus('saved');
        await loadTaxData();
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('unsaved');
      } finally {
        saveTimeoutRef.current = null;
      }
    }, 2000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [manualAdjustments, isDirty, taxYear]);

  // Auto-save Filing Info when fields change (save as you go)
  useEffect(() => {
    if (activeTab !== 'filing-info') return; // Only when user is on Filing Info tab
    const status = watchedFilingStatus;
    const deps = watchedNumberOfDependents;
    const under17 = watchedDependentsUnder17;
    const age59 = watchedEstimatedAge59;
    const foreign = watchedForeignIncomeExclusion;
    if (status === undefined && deps === undefined) return; // Initial load
    if (filingInfoSaveRef.current) clearTimeout(filingInfoSaveRef.current);
    filingInfoSaveRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('accessToken');
        await axios.post('/api/tax-forms/save-filing-info', {
          taxYear,
          filingInfo: {
            filing_status: status ?? filingInfo.filingStatus,
            number_of_dependents: parseInt(deps ?? filingInfo.numberOfDependents) || 0,
            dependents_under_17: parseInt(under17 ?? filingInfo.dependentsUnder17) || 0,
            estimated_age_59: age59 ?? filingInfo.estimatedAge59,
            foreign_income_exclusion: foreign ?? filingInfo.foreignIncomeExclusion
          }
        }, { headers: { Authorization: `Bearer ${token}` } });
        setFilingInfo(prev => ({
          ...prev,
          filingStatus: status ?? prev.filingStatus,
          numberOfDependents: parseInt(deps ?? prev.numberOfDependents) || 0,
          dependentsUnder17: parseInt(under17 ?? prev.dependentsUnder17) || 0,
          estimatedAge59: age59 ?? prev.estimatedAge59,
          foreignIncomeExclusion: foreign ?? prev.foreignIncomeExclusion
        }));
        await loadTaxData();
      } catch (e) { /* silent */ }
      filingInfoSaveRef.current = null;
    }, 1500);
    return () => { if (filingInfoSaveRef.current) clearTimeout(filingInfoSaveRef.current); };
  }, [activeTab, watchedFilingStatus, watchedNumberOfDependents, watchedDependentsUnder17, watchedEstimatedAge59, watchedForeignIncomeExclusion, taxYear]);

  const loadFilingInfo = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/tax-forms/get-filing-info?taxYear=${taxYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const filingData = {
          filingStatus: response.data.data.filing_status || 'married_filing_jointly',
          numberOfDependents: response.data.data.number_of_dependents || 0,
          dependentsUnder17: response.data.data.dependents_under_17 || 0,
          estimatedAge59: response.data.data.estimated_age_59 || false,
          foreignIncomeExclusion: response.data.data.foreign_income_exclusion || false
        };
        setFilingInfo(filingData);
        // Reset form with loaded data using setValue
        setFilingInfoValue('filingStatus', filingData.filingStatus);
        setFilingInfoValue('numberOfDependents', filingData.numberOfDependents);
        setFilingInfoValue('dependentsUnder17', filingData.dependentsUnder17);
        setFilingInfoValue('estimatedAge59', filingData.estimatedAge59);
        setFilingInfoValue('foreignIncomeExclusion', filingData.foreignIncomeExclusion);
      }
    } catch (error) {
      console.error('Error loading filing info:', error);
      // Don't show error, just use defaults
    }
  };

  const loadAdjustments = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/tax-forms/get-adjustments?taxYear=${taxYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setManualAdjustments(response.data.data || {});
        setSaveStatus('saved');
      }
    } catch (error) {
      console.error('Error loading adjustments:', error);
      // Don't show error, just use empty adjustments
    }
  };

  const loadTaxData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/tax-1040/prepare?taxYear=${taxYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFormData(response.data.data.form_1040);
        setRawData(response.data.data.raw_data || {});
        setIsDirty(false);
      }
    } catch (error) {
      console.error('Error loading tax data:', error);
      toast.error('Failed to load tax data');
    } finally {
      setLoading(false);
    }
  };

  const handleManualAdjustment = (formName, fieldName, value) => {
    const key = `${formName}.${fieldName}`;
    setManualAdjustments(prev => ({
      ...prev,
      [key]: value
    }));
    setIsDirty(true);
  };

  const saveFilingInfo = async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const filingData = {
        filing_status: data.filingStatus,
        number_of_dependents: parseInt(data.numberOfDependents) || 0,
        dependents_under_17: parseInt(data.dependentsUnder17) || 0,
        estimated_age_59: data.estimatedAge59 || false,
        foreign_income_exclusion: data.foreignIncomeExclusion || false
      };

      await axios.post('/api/tax-forms/save-filing-info', {
        taxYear,
        filingInfo: filingData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setFilingInfo({
        filingStatus: data.filingStatus,
        numberOfDependents: parseInt(data.numberOfDependents) || 0,
        dependentsUnder17: parseInt(data.dependentsUnder17) || 0,
        estimatedAge59: data.estimatedAge59 || false,
        foreignIncomeExclusion: data.foreignIncomeExclusion || false
      });

      toast.success('Filing information saved successfully');
      // Reload tax data to recalculate with new filing info
      await loadTaxData();
    } catch (error) {
      console.error('Error saving filing info:', error);
      toast.error('Failed to save filing information');
    }
  };

  const saveManualAdjustments = useCallback(async (yearToSave = null) => {
    const year = yearToSave ?? taxYear;
    const adjustments = manualAdjustments;
    if (Object.keys(adjustments).length === 0) {
      setIsDirty(false);
      return;
    }
    try {
      setSaveStatus('saving');
      const token = localStorage.getItem('accessToken');
      await axios.post('/api/tax-forms/save-adjustments', {
        taxYear: year,
        adjustments
      }, { headers: { Authorization: `Bearer ${token}` } });
      setIsDirty(false);
      setSaveStatus('saved');
      toast.success(year === taxYear ? 'Saved' : `Saved ${year} data`);
      if (year === taxYear) await loadTaxData();
    } catch (error) {
      console.error('Error saving adjustments:', error);
      setSaveStatus('unsaved');
      toast.error('Failed to save adjustments');
    }
  }, [taxYear, manualAdjustments]);

  const handleTaxYearChange = useCallback(async (e) => {
    const newYear = parseInt(e.target.value);
    if (newYear === taxYear) return;
    if (isDirty && Object.keys(manualAdjustments).length > 0) {
      await saveManualAdjustments(taxYear);
    }
    try {
      sessionStorage.setItem(TAX_YEAR_STORAGE_KEY, String(newYear));
    } catch (e) { /* ignore */ }
    setTaxYear(newYear);
  }, [taxYear, isDirty, manualAdjustments, saveManualAdjustments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getAdjustedValue = (formName, fieldName, defaultValue = 0) => {
    const key = `${formName}.${fieldName}`;
    if (manualAdjustments.hasOwnProperty(key)) {
      return parseFloat(manualAdjustments[key]) || 0;
    }
    return defaultValue;
  };

  // Calculate totals — prefer server-computed values from tax-1040/prepare
  const calculateIncome = () => {
    return getAdjustedValue('1040', 'total_income', formData.total_income || 0);
  };

  const getTotalRentalIncome = () => {
    return formData.rental_summary?.gross_rental_income
      ?? formData.rental_properties?.reduce((sum, prop) => sum + (prop.annual_rental_income || 0), 0)
      ?? 0;
  };

  const getNetRentalIncome = () => {
    return formData.rental_summary?.net_rental_income
      ?? formData.rental_properties?.reduce((sum, prop) => sum + (prop.net_rental_income || 0), 0)
      ?? 0;
  };

  const incomeTypeLabel = (type) => ({
    w2_wages: 'W-2 Wages',
    rental: 'Rental Income',
    dividend: 'Dividends',
    interest: 'Interest',
    capital_gain: 'Capital Gain',
    capital_loss: 'Capital Loss',
    roth_withdrawal: 'Roth Withdrawal',
    business_income: 'Business Income',
    other: 'Other'
  }[type] || type);

  const handleAddRothWithdrawal = async (e) => {
    e.preventDefault();
    const amt = parseFloat(rothForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid withdrawal amount');
      return;
    }
    setRothSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post('/api/tax-1040/roth-withdrawal', {
        taxYear,
        withdrawalDate: rothForm.withdrawalDate || `${taxYear}-06-01`,
        amount: amt,
        isQualified: rothForm.isQualified,
        notes: rothForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Roth withdrawal added');
      setRothForm({ withdrawalDate: '', amount: '', isQualified: false, notes: '' });
      await loadTaxData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add Roth withdrawal');
    } finally {
      setRothSaving(false);
    }
  };

  const handleDeleteRothWithdrawal = async (id) => {
    if (!window.confirm('Remove this Roth withdrawal?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`/api/tax-1040/roth-withdrawal/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed');
      await loadTaxData();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Tax Tools - Complete your return */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5">
        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
          <FileText className="h-4 w-4 mr-2" />
          Complete your return
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Fill out guided forms, then export a filled Form 1040 PDF to e-file yourself.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/property-tax-questionnaire" className="inline-flex items-center px-4 py-2 bg-white border border-blue-200 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 shadow-sm">
            <Building2 className="h-4 w-4 mr-2" />
            Rental Property (Schedule E)
          </Link>
          <Link to="/expense-tracker" className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-200 rounded-md text-sm text-blue-700 hover:bg-blue-100">
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Expense Tracker
          </Link>
          <Link to="/income-tracker" className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-200 rounded-md text-sm text-blue-700 hover:bg-blue-100">
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Income Tracker
          </Link>
          <Link to="/property-manager" className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-200 rounded-md text-sm text-blue-700 hover:bg-blue-100">
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Property Manager
          </Link>
        </div>
      </div>

      {/* Deduction Suggestions - personalized based on profile */}
      {deductionSuggestions.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
          <button
            onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-amber-100/50 transition"
          >
            <h4 className="text-sm font-semibold text-amber-900 flex items-center">
              <Lightbulb className="h-4 w-4 mr-2 text-amber-600" />
              Deduction suggestions for your situation
            </h4>
            {suggestionsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {suggestionsExpanded && (
            <div className="px-4 pb-4 max-h-80 overflow-y-auto">
              <p className="text-xs text-amber-800 mb-3">Based on your rentals, solar, child, and Roth withdrawal.</p>
              <div className="space-y-2">
                {deductionSuggestions.map((s) => (
                  <div key={s.id} className="bg-white rounded-lg border border-amber-100 p-3 text-sm">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <span className="font-medium text-gray-900">{s.title}</span>
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{s.category}</span>
                      </div>
                      {s.link && (
                        <Link to={s.link} className="text-blue-600 hover:underline text-xs shrink-0">Add →</Link>
                      )}
                    </div>
                    <p className="text-gray-600 mt-1 text-xs">{s.desc}</p>
                    {s.form && <span className="text-xs text-gray-500 mt-1 block">Form: {s.form}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Basic Filing Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Eye className="h-5 w-5 mr-2 text-blue-600" />
          Form 1040 - U.S. Individual Income Tax Return
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Data from bank statement imports, expense tracker, properties, and taxable accounts. Edit values in the Form 1040 tab.
        </p>
        
        {/* Filing Status & Tax Year */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-6 border-b">
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">Tax Year</div>
            <div className="text-xl font-bold text-gray-900">{formData.tax_year || taxYear}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">Filing Status</div>
            <div className="text-xl font-bold text-blue-600 capitalize">
              {formData.filing_status?.replace(/_/g, ' ') || 'Married Filing Jointly'}
            </div>
            <p className="text-xs text-gray-500 mt-1 flex items-center">
              <Info className="h-3.5 w-3.5 mr-1" />
              Married filing jointly often gets the best standard deduction
            </p>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase mb-1">Dependents</div>
            <div className="text-xl font-bold text-gray-900">
              {formData.number_of_dependents || 0} Total
              {formData.dependents_under_17 > 0 && (
                <span className="text-base ml-2 text-green-600">
                  ({formData.dependents_under_17} Under 17)
                </span>
              )}
            </div>
            {formData.dependents_under_17 > 0 && (
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Info className="h-3.5 w-3.5 mr-1" />
                Each child under 17 = $2,000 Child Tax Credit
              </p>
            )}
          </div>
        </div>
        
        {/* INCOME SECTION */}
        <div className="mb-6">
          <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-blue-500 pl-3">
            INCOME (Lines 1-8b)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">
                    Line 1: Wages, Salaries, Tips
                    {(getAdjustedValue('1040', 'wages', formData.wages || 0) === 0 && (formData.grossIncome?.w2Wages || 0) === 0) && (
                      <Link to="/income-tracker" className="ml-2 text-xs text-blue-600 hover:underline">
                        Add in Income Tracker →
                      </Link>
                    )}
                  </span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(getAdjustedValue('1040', 'wages', formData.wages || 0))}</span>
                </div>
                {(formData.grossIncome?.w2Wages || 0) > 0 && (
                  <div className="flex justify-between items-center text-blue-700">
                    <span className="text-sm font-semibold flex items-center gap-1">
                      W-2 Wages
                      <span className="text-xs font-normal px-1.5 py-0.5 bg-blue-100 rounded">From Income Tracker</span>
                    </span>
                    <span className="font-mono font-bold text-lg">{formatCurrency(formData.grossIncome.w2Wages || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Line 2b: Taxable Interest Income</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(getAdjustedValue('1040', 'interest_income', formData.interest_income || 0))}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Line 3b: Ordinary Dividends</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(getAdjustedValue('1040', 'dividend_income', formData.dividend_income || 0))}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Line 7: Capital Gains</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(formData.capital_gains || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Line 8: Net Rental (Schedule E)</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(getNetRentalIncome())}</span>
                </div>
                {(formData.roth_withdrawals || 0) > 0 && (
                  <div className="flex justify-between items-center text-purple-700">
                    <span className="text-sm font-semibold flex items-center gap-1">
                      Roth IRA (taxable portion)
                      <Link to="/tax-forms?tab=income-sources" className="text-xs text-purple-600 hover:underline">Manage →</Link>
                    </span>
                    <span className="font-mono font-bold">{formatCurrency(formData.roth_withdrawals || 0)}</span>
                  </div>
                )}
                {getTotalRentalIncome() > 0 && (
                  <div className="flex justify-between items-center text-green-700 text-sm">
                    <span>Gross rental (before expenses)</span>
                    <span className="font-mono">{formatCurrency(getTotalRentalIncome())}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t-2 border-blue-300 pt-2 mt-2">
                  <span className="text-sm font-bold text-gray-800">Total Income (Line 8b)</span>
                  <span className="font-mono font-bold text-xl">{formatCurrency(calculateIncome())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ADJUSTMENTS SECTION */}
        {formData.adjustments && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-purple-500 pl-3">
              ADJUSTMENTS (Lines 9-26)
            </h4>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>IRA Deductions</span>
                  <span className="font-mono">{formatCurrency(formData.adjustments.ira_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Student Loan Interest</span>
                  <span className="font-mono">{formatCurrency(formData.adjustments.student_loan_interest || 0)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Adjusted Gross Income (Line 11)</span>
                  <span className="font-mono font-semibold">{formatCurrency(formData.adjusted_gross_income || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DEDUCTIONS SECTION - Schedule A Itemized */}
        <div className="mb-6">
          <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-green-500 pl-3">
            DEDUCTIONS (Lines 12-24) — Standard or Schedule A
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Line 12: Standard Deduction</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(formData.standard_deduction || 0)}</span>
              </div>
              <p className="text-xs text-gray-600">Based on filing status</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">Schedule A — Itemized Deductions</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>State & Local Taxes (SALT cap $10k)</span>
                  <span className="font-mono">{formatCurrency(formData.itemized_deductions?.state_local_taxes || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Mortgage Interest (Schedule A)</span>
                  <span className="font-mono">{formatCurrency(formData.itemized_deductions?.mortgage_interest || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Charitable Contributions</span>
                  <span className="font-mono">{formatCurrency(formData.itemized_deductions?.charitable_contributions || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Itemized</span>
                  <span className="font-mono">{formatCurrency(formData.itemized_deductions?.other_itemized || 0)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Total Itemized</span>
                  <span className="font-mono font-semibold">{formatCurrency(formData.total_itemized_deductions || 0)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-gray-800">Using:</span>
              <span className="font-bold text-2xl text-blue-600 uppercase">{formData.deduction_type?.toUpperCase() || 'STANDARD'}</span>
              <span className="font-bold text-gray-800">Total Deductions:</span>
              <span className="font-mono font-bold text-xl">{formatCurrency(formData.total_deductions || 0)}</span>
            </div>
            <p className="text-xs text-gray-600 flex items-center">
              <Info className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
              {formData.deduction_type === 'standard'
                ? "You're using Standard because it's higher than itemized"
                : "You're using Itemized deductions (Schedule A)"}
            </p>
          </div>
        </div>

        {/* TAX CALCULATION - Form 1040 Lines 15-24 */}
        <div className="mb-6">
          <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-red-500 pl-3">
            TAX & CREDITS (Lines 15-24)
          </h4>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">Line 15: Taxable Income</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(formData.taxable_income || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">Line 16: Tax Before Credits</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(formData.estimated_tax_before_credits || 0)}</span>
              </div>
              <div className="flex justify-between items-center border-t-2 border-red-300 pt-2 mt-2">
                <span className="text-sm font-bold text-gray-800">Line 24: Total Tax (after credits)</span>
                <span className="font-mono font-bold text-2xl text-red-600">{formatCurrency(formData.estimated_tax || 0)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Effective Rate: {formData.summary?.effective_rate || '0.00'}%
              </div>
            </div>
          </div>
        </div>

        {/* CREDITS */}
        {formData.credits && Object.values(formData.credits).some(v => v > 0) && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-yellow-500 pl-3">
              TAX CREDITS
            </h4>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="space-y-2">
                {formData.credits.child_tax_credit > 0 && (
                  <div className="flex justify-between">
                    <span className="font-semibold">Child Tax Credit ({formData.dependents_under_17 || 0} children)</span>
                    <span className="font-mono font-bold text-lg text-green-600">{formatCurrency(formData.credits.child_tax_credit)}</span>
                  </div>
                )}
                {formData.credits.solar_tax_credit > 0 && (
                  <div className="flex justify-between">
                    <span className="font-semibold">Solar Tax Credit (ITC)</span>
                    <span className="font-mono font-bold text-lg text-green-600">{formatCurrency(formData.credits.solar_tax_credit)}</span>
                  </div>
                )}
                {formData.credits.other_credits > 0 && (
                  <div className="flex justify-between">
                    <span className="font-semibold">Other Credits</span>
                    <span className="font-mono font-bold text-lg text-green-600">{formatCurrency(formData.credits.other_credits)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PROPERTIES */}
        {formData.rental_properties && formData.rental_properties.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-indigo-500 pl-3">
              RENTAL PROPERTIES (Schedule E)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.rental_properties.map((prop, idx) => (
                <div key={idx} className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200">
                  <h5 className="font-bold text-indigo-900 mb-3">{prop.name}</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Property Value</span>
                      <span className="font-mono">{formatCurrency(prop.value || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cost Basis (Purchase Price)</span>
                      <span className="font-mono">{formatCurrency(prop.cost_basis || 0)}</span>
                    </div>
                    {prop.rental_start_date && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Rental Since</span>
                        <span>{new Date(prop.rental_start_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {prop.years_depreciated !== undefined && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Years Depreciated</span>
                        <span>{prop.years_depreciated} / 27.5 years</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-2">
                      <span>Annual Depreciation</span>
                      <span className="font-mono text-lg">
                        {prop.depreciation > 0 ? formatCurrency(prop.depreciation) : <span className="text-red-500">Depleted</span>}
                      </span>
                    </div>
                    {prop.annual_rental_income && prop.annual_rental_income > 0 && (
                      <div className="flex justify-between text-green-700">
                        <span>Annual Rental Income</span>
                        <span className="font-mono font-semibold">{formatCurrency(prop.annual_rental_income || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Net Rental Income</span>
                      <span className="font-mono">{formatCurrency(prop.net_rental_income || 0)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MORTGAGE INTEREST BY PROPERTY */}
        {formData.mortgage_interest_by_property && formData.mortgage_interest_by_property.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-cyan-500 pl-3">
              MORTGAGE INTEREST BY PROPERTY
            </h4>
            <div className="bg-cyan-50 rounded-lg p-4">
              <div className="space-y-2">
                {formData.mortgage_interest_by_property.map((prop, idx) => (
                  <div key={idx} className="flex justify-between items-center border-b pb-2 last:border-0">
                    <span className="font-medium">{prop.property_name}</span>
                    <span className="font-mono font-semibold">{formatCurrency(prop.mortgage_interest || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PROPERTY TAXES BY PROPERTY */}
        {formData.property_taxes_by_property && formData.property_taxes_by_property.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-orange-500 pl-3">
              PROPERTY TAXES BY PROPERTY
            </h4>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="space-y-2">
                {formData.property_taxes_by_property.map((prop, idx) => (
                  <div key={idx} className="flex justify-between items-center border-b pb-2 last:border-0">
                    <span className="font-medium">{prop.property_name}</span>
                    <span className="font-mono font-semibold">{formatCurrency(prop.property_taxes || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* DETAILED EXPENSES */}
        {rawData.expenses && (
          <div className="mb-6">
            <h4 className="text-md font-bold text-gray-800 mb-4 border-l-4 border-teal-500 pl-3 flex items-center gap-2">
              DETAILED DEDUCTIBLE EXPENSES BY CATEGORY
              <span className="text-xs font-normal px-1.5 py-0.5 bg-teal-100 rounded">From Expense Tracker</span>
            </h4>
            <div className="bg-teal-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {rawData.expenses.insurance > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Insurance</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.insurance)}</span>
                  </div>
                )}
                {rawData.expenses.maintenance > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Maintenance</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.maintenance)}</span>
                  </div>
                )}
                {rawData.expenses.utilities > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Utilities</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.utilities)}</span>
                  </div>
                )}
                {rawData.expenses.repairs > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Repairs</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.repairs)}</span>
                  </div>
                )}
                {rawData.expenses.professional_services > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Professional Services</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.professional_services)}</span>
                  </div>
                )}
                {rawData.expenses.business_meals > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Business Meals</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.business_meals)}</span>
                  </div>
                )}
                {rawData.expenses.travel > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Travel</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.travel)}</span>
                  </div>
                )}
                {rawData.expenses.office_supplies > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Office Supplies</span>
                    <span className="font-mono font-semibold">{formatCurrency(rawData.expenses.office_supplies)}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-teal-300">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Total Deductible Expenses</span>
                  <span className="font-mono font-bold text-xl">{formatCurrency(rawData.expenses.total_deductible || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderForm1040 = () => (
    <div className="space-y-6 pb-12">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-600" />
          Form 1040 - Individual Income Tax Return
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Edit values below. Totals are calculated from your expenses, income, and properties. See Overview tab for full summary.
        </p>
        
        <div className="space-y-6">
          {/* Income Section (Lines 1-8b) */}
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-semibold text-gray-800 mb-3">Income (Lines 1-8b)</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 1: Wages, Salaries, Tips</label>
                <input
                  type="number"
                  value={getAdjustedValue('1040', 'wages', formData.wages || 0)}
                  onChange={(e) => handleManualAdjustment('1040', 'wages', e.target.value)}
                  className="md:col-span-2 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 2b: Taxable Interest</label>
                <input
                  type="number"
                  value={getAdjustedValue('1040', 'interest_income', formData.interest_income || 0)}
                  onChange={(e) => handleManualAdjustment('1040', 'interest_income', e.target.value)}
                  className="md:col-span-2 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 3b: Ordinary Dividends</label>
                <input
                  type="number"
                  value={getAdjustedValue('1040', 'dividend_income', formData.dividend_income || 0)}
                  onChange={(e) => handleManualAdjustment('1040', 'dividend_income', e.target.value)}
                  className="md:col-span-2 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 5b: Rental Income (Schedule E)</label>
                <div className="md:col-span-2 border border-gray-300 rounded px-3 py-2 bg-gray-50 text-sm font-mono text-gray-700">
                  {formatCurrency(getTotalRentalIncome())}
                </div>
                <span className="md:col-span-3 text-xs text-gray-500">From rental properties</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 7: Business Income</label>
                <input
                  type="number"
                  value={getAdjustedValue('1040', 'business_income', formData.business_income || 0)}
                  onChange={(e) => handleManualAdjustment('1040', 'business_income', e.target.value)}
                  className="md:col-span-2 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  placeholder="0.00"
                />
              </div>
              <div className="flex justify-between items-center border-t pt-3 mt-3">
                <span className="text-sm font-bold text-gray-800">Line 8b: Total Income</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(calculateIncome())}</span>
              </div>
            </div>
          </div>

          {/* Adjustments (Lines 9-11) */}
          {formData.adjustments && (
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-3">Adjustments (Lines 9-11)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>IRA Deductions</span>
                  <span className="font-mono">{formatCurrency(formData.adjustments.ira_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Student Loan Interest</span>
                  <span className="font-mono">{formatCurrency(formData.adjustments.student_loan_interest || 0)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 font-semibold">
                  <span>Line 11: Adjusted Gross Income</span>
                  <span className="font-mono">{formatCurrency(formData.adjusted_gross_income || 0)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Deductions (Lines 12-24) */}
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-semibold text-gray-800 mb-3">Deductions (Lines 12-24)</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Line 12: Standard Deduction</label>
                <div className="md:col-span-2">
                  <div className="border border-gray-300 rounded px-3 py-2 bg-gray-50 text-sm font-mono text-gray-700">
                    {formatCurrency(formData.standard_deduction || 0)}
                  </div>
                  <span className="text-xs text-gray-500">Auto-calculated based on filing status</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs font-semibold text-gray-600 mb-2">Schedule A — Itemized Deductions</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>State & Local Taxes (SALT cap $10k)</span>
                    <span className="font-mono">{formatCurrency(formData.itemized_deductions?.state_local_taxes || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Mortgage Interest</span>
                    <span className="font-mono">{formatCurrency(formData.itemized_deductions?.mortgage_interest || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Charitable Contributions</span>
                    <span className="font-mono">{formatCurrency(formData.itemized_deductions?.charitable_contributions || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Itemized</span>
                    <span className="font-mono">{formatCurrency(formData.itemized_deductions?.other_itemized || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-semibold">
                    <span>Total Itemized</span>
                    <span className="font-mono">{formatCurrency(formData.total_itemized_deductions || 0)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <span className="font-bold text-gray-800">Using: {formData.deduction_type?.toUpperCase() || 'STANDARD'}</span>
                <span className="font-mono font-bold">Total Deductions: {formatCurrency(formData.total_deductions || 0)}</span>
              </div>
            </div>
          </div>

          {/* Tax Summary (Lines 15-24) */}
          <div className="border-l-4 border-red-500 pl-4">
            <h4 className="font-semibold text-gray-800 mb-3">Tax Calculation (Lines 15-24)</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold">Line 15: Taxable Income</span>
                <span className="font-mono font-bold">{formatCurrency(formData.taxable_income || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Line 16: Tax Before Credits</span>
                <span className="font-mono font-bold">{formatCurrency(formData.estimated_tax_before_credits || 0)}</span>
              </div>
              <div className="flex justify-between border-t-2 border-red-200 pt-3 mt-2">
                <span className="font-bold text-gray-800">Line 24: Total Tax (after credits)</span>
                <span className="font-mono font-bold text-lg text-red-600">{formatCurrency(formData.estimated_tax || 0)}</span>
              </div>
              {formData.summary?.effective_rate != null && (
                <div className="text-xs text-gray-500 mt-1">Effective Rate: {formData.summary.effective_rate}%</div>
              )}
            </div>
          </div>

          {/* Credits Section (Lines 25-32) */}
          <div className="border-l-4 border-purple-500 pl-4">
            <h4 className="font-semibold text-gray-800 mb-3">Credits (Lines 25-32)</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Child Tax Credit</label>
                <div className="md:col-span-2">
                  <input
                    type="number"
                    value={getAdjustedValue('1040', 'child_tax_credit', formData.credits?.child_tax_credit || 0)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                        handleManualAdjustment('1040', 'child_tax_credit', value);
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700 flex items-center">
                  Solar installation cost
                  <span className="ml-2 text-xs text-gray-500" title="2025: 30% of total cost = your credit (Residential Clean Energy Credit)">?</span>
                </label>
                <div className="md:col-span-2 space-y-1">
                  <input
                    type="number"
                    value={getAdjustedValue('1040', 'solar_installation_cost', '') || (() => {
                      const cr = parseFloat(getAdjustedValue('1040', 'solar_tax_credit', 0)) || 0;
                      return cr > 0 ? (cr / 0.30).toFixed(2) : '';
                    })()}
                    onChange={(e) => {
                      const costVal = e.target.value;
                      if (costVal === '' || (!isNaN(parseFloat(costVal)) && parseFloat(costVal) >= 0)) {
                        handleManualAdjustment('1040', 'solar_installation_cost', costVal);
                        const cost = parseFloat(costVal) || 0;
                        handleManualAdjustment('1040', 'solar_tax_credit', cost > 0 ? (cost * 0.30).toFixed(2) : '');
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 25000"
                    step="0.01"
                    min="0"
                  />
                  <p className="text-xs text-gray-500">
                    2025 credit: 30% of cost = {formatCurrency(parseFloat(getAdjustedValue('1040', 'solar_tax_credit', formData.credits?.solar_tax_credit || 0)) || 0)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-700">Other Credits</label>
                <div className="md:col-span-2">
                  <input
                    type="number"
                    value={getAdjustedValue('1040', 'other_credits', formData.credits?.other_credits || 0)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                        handleManualAdjustment('1040', 'other_credits', value);
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SCHEDULE_E_LINES = [
    { key: 'line3_rents_received', label: 'Line 3: Rents received' },
    { key: 'line5_advertising', label: 'Line 5: Advertising' },
    { key: 'line6_auto_travel', label: 'Line 6: Auto and travel' },
    { key: 'line7_cleaning_maintenance', label: 'Line 7: Cleaning and maintenance' },
    { key: 'line8_commissions', label: 'Line 8: Commissions' },
    { key: 'line9_insurance', label: 'Line 9: Insurance' },
    { key: 'line10_legal_professional', label: 'Line 10: Legal and professional fees' },
    { key: 'line11_management_fees', label: 'Line 11: Management fees' },
    { key: 'line12_mortgage_interest', label: 'Line 12: Mortgage interest' },
    { key: 'line13_other_interest', label: 'Line 13: Other interest' },
    { key: 'line14_repairs', label: 'Line 14: Repairs' },
    { key: 'line15_supplies', label: 'Line 15: Supplies' },
    { key: 'line16_taxes', label: 'Line 16: Taxes' },
    { key: 'line17_utilities', label: 'Line 17: Utilities' },
    { key: 'line18_depreciation', label: 'Line 18: Depreciation' },
    { key: 'line19_other', label: 'Line 19: Other' },
    { key: 'line20_total_expenses', label: 'Line 20: Total expenses', isTotal: true },
    { key: 'line21_income_or_loss', label: 'Line 21: Income or (loss)', isNet: true }
  ];

  const renderScheduleE = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Home className="h-5 w-5 mr-2 text-blue-600" />
          Schedule E - Supplemental Income and Loss (Rental Properties)
        </h3>
        <p className="text-sm text-gray-600 mb-4">Full IRS Schedule E Part I breakdown. Use the Property Questionnaire to add or update data.</p>
        
        {formData.rental_properties && formData.rental_properties.length > 0 ? (
          <div className="space-y-6">
            {formData.rental_properties.map((property, idx) => {
              const se = property.schedule_e_breakdown || {};
              return (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">{property.name}</h4>
                  <div className="space-y-2 text-sm">
                    {SCHEDULE_E_LINES.map(({ key, label, isTotal, isNet }) => {
                      const val = se[key];
                      const numVal = typeof val === 'number' ? val : parseFloat(val);
                      const hasVal = typeof numVal === 'number' && !isNaN(numVal);
                      const show = hasVal || isTotal || isNet;
                      if (!show) return null;
                      const displayVal = hasVal
                        ? (numVal < 0 ? `(${formatCurrency(Math.abs(numVal))})` : formatCurrency(numVal))
                        : '—';
                      return (
                        <div key={key} className={`flex justify-between ${isTotal || isNet ? 'border-t pt-2 font-semibold' : ''}`}>
                          <span>{label}</span>
                          <span className={`font-mono ${numVal < 0 ? 'text-red-600' : ''}`}>{displayVal}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No rental properties data available. Complete the <Link to="/property-tax-questionnaire" className="text-blue-600 hover:underline">Property Questionnaire</Link> to add rental data.</p>
        )}
      </div>
    </div>
  );

  const renderFilingInfo = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            Filing Information
          </h3>
          <span className="text-sm text-gray-500">Changes save automatically</span>
        </div>
        
        <form onSubmit={handleSubmitFilingInfo(saveFilingInfo)}>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-3">Filing Status</h4>
              <ValidatedSelect
                name="filingStatus"
                label="Filing Status"
                register={registerFilingInfo}
                errors={filingInfoErrors}
                placeholder="Select filing status"
                required
                defaultValue={filingInfo.filingStatus}
                options={[
                  { value: 'single', label: 'Single' },
                  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
                  { value: 'married_filing_separately', label: 'Married Filing Separately' },
                  { value: 'head_of_household', label: 'Head of Household' },
                  { value: 'qualifying_widow', label: 'Qualifying Widow(er)' }
                ]}
                {...registerFilingInfo('filingStatus', {
                  required: 'Filing status is required'
                })}
              />
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-3">Dependents</h4>
              <div className="space-y-3">
                <ValidatedInput
                  name="numberOfDependents"
                  label="Total Dependents"
                  type="number"
                  register={registerFilingInfo}
                  errors={filingInfoErrors}
                  placeholder="0"
                  min="0"
                  defaultValue={filingInfo.numberOfDependents}
                  {...registerFilingInfo('numberOfDependents', {
                    required: 'Number of dependents is required',
                    min: {
                      value: 0,
                      message: 'Number of dependents cannot be negative'
                    },
                    max: {
                      value: 20,
                      message: 'Number of dependents cannot exceed 20'
                    },
                    validate: (value) => {
                      const num = parseInt(value);
                      if (isNaN(num) || num < 0) {
                        return 'Please enter a valid number';
                      }
                      return true;
                    }
                  })}
                />
                
                <ValidatedInput
                  name="dependentsUnder17"
                  label="Dependents Under 17 (Child Tax Credit)"
                  type="number"
                  register={registerFilingInfo}
                  errors={filingInfoErrors}
                  placeholder="0"
                  min="0"
                  max={watchedNumberOfDependents || filingInfo.numberOfDependents}
                  defaultValue={filingInfo.dependentsUnder17}
                  {...registerFilingInfo('dependentsUnder17', {
                    required: 'Dependents under 17 is required',
                    min: {
                      value: 0,
                      message: 'Cannot be negative'
                    },
                    validate: (value) => {
                      const num = parseInt(value);
                      const total = parseInt(watchedNumberOfDependents || filingInfo.numberOfDependents);
                      if (isNaN(num) || num < 0) {
                        return 'Please enter a valid number';
                      }
                      if (num > total) {
                        return `Cannot exceed total dependents (${total})`;
                      }
                      return true;
                    }
                  })}
                />
              </div>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-3">Additional Information</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="age59"
                    {...registerFilingInfo('estimatedAge59')}
                    checked={!!watchFilingInfo('estimatedAge59')}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="age59" className="text-sm text-gray-700">
                    Age 59½ or Older (affects retirement account withdrawals)
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="foreignExclusion"
                    {...registerFilingInfo('foreignIncomeExclusion')}
                    checked={!!watchFilingInfo('foreignIncomeExclusion')}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="foreignExclusion" className="text-sm text-gray-700">
                    Eligible for Foreign Earned Income Exclusion (Costa Rica residence)
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
              >
                <Save className="h-4 w-4 inline mr-2" />
                Save Filing Info
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  const renderManualAdjustments = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">Manual Adjustments</h3>
        <p className="text-sm text-yellow-700">
          Use this section to manually override calculated values. Changes will recalculate all dependent fields.
        </p>
      </div>

      {Object.keys(manualAdjustments).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Settings className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No manual adjustments made yet</p>
          <p className="text-sm text-gray-400 mt-2">
            Edit values in any form tab to create manual adjustments
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Current Adjustments</h3>
          <div className="space-y-2">
            {Object.entries(manualAdjustments).map(([key, value]) => (
              <div key={key} className="flex justify-between items-center border-b pb-2">
                <span className="text-sm text-gray-600 font-mono">{key}</span>
                <span className="font-semibold">{formatCurrency(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderIncomeSources = () => {
    const transactions = formData.income_transactions || rawData.income_transactions || [];
    const rothList = formData.roth_withdrawals_list || rawData.roth_withdrawals || [];
    const rentalSummary = formData.rental_summary || rawData.rental_summary || {};

    return (
      <div className="space-y-6">
        {/* Rental summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Home className="h-5 w-5 mr-2 text-green-600" />
            Rental Income & Expenses (Schedule E)
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Pulled from bank statement imports (Income Tracker) and rental expenses (Expense Tracker).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase">Gross Rental Income</div>
              <div className="text-2xl font-bold text-green-700">{formatCurrency(rentalSummary.gross_rental_income || getTotalRentalIncome())}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase">Total Rental Expenses</div>
              <div className="text-2xl font-bold text-red-700">{formatCurrency(rentalSummary.total_rental_expenses || 0)}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase">Net Rental (Line 8)</div>
              <div className="text-2xl font-bold text-blue-700">{formatCurrency(rentalSummary.net_rental_income || getNetRentalIncome())}</div>
            </div>
          </div>
          {formData.rental_properties?.length > 0 && (
            <div className="space-y-3">
              {formData.rental_properties.map((prop, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold">{prop.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-200">
                      {prop.rental_income_source === 'bank_import' ? 'From bank imports' : 'From property config'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-gray-500">Gross rent:</span> {formatCurrency(prop.annual_rental_income)}</div>
                    <div><span className="text-gray-500">Expenses:</span> {formatCurrency(prop.total_rental_expenses || 0)}</div>
                    <div><span className="text-gray-500">Net:</span> <strong>{formatCurrency(prop.net_rental_income)}</strong></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex gap-3">
            <Link to="/expense-tracker" className="text-sm text-blue-600 hover:underline">→ Expense Tracker</Link>
            <Link to="/property-tax-questionnaire" className="text-sm text-blue-600 hover:underline">→ Property Questionnaire</Link>
          </div>
        </div>

        {/* All taxable income */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
            All Taxable Income ({taxYear})
          </h3>
          {transactions.length === 0 ? (
            <p className="text-gray-500 text-sm">No income recorded. Import bank statements or add in Income Tracker.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 pr-4">Property</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 whitespace-nowrap">{tx.transaction_date}</td>
                      <td className="py-2 pr-4">
                        <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          {incomeTypeLabel(tx.transaction_type)}
                        </span>
                      </td>
                      <td className="py-2 pr-4 max-w-xs truncate">{tx.description}</td>
                      <td className="py-2 pr-4 text-gray-500">{tx.property_name || '—'}</td>
                      <td className="py-2 text-right font-mono font-semibold">{formatCurrency(tx.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Link to="/income-tracker" className="inline-block mt-4 text-sm text-blue-600 hover:underline">→ Income Tracker</Link>
        </div>

        {/* Roth IRA withdrawals */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-2 flex items-center">
            <PiggyBank className="h-5 w-5 mr-2 text-purple-600" />
            Roth IRA Withdrawals
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Qualified withdrawals are tax-free. Non-qualified withdrawals are added to taxable income and may incur a 10% early withdrawal penalty.
          </p>

          <form onSubmit={handleAddRothWithdrawal} className="bg-purple-50 rounded-lg p-4 mb-6 border border-purple-100">
            <h4 className="font-medium text-purple-900 mb-3">Add Roth Withdrawal</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  value={rothForm.withdrawalDate}
                  onChange={(e) => setRothForm(f => ({ ...f, withdrawalDate: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={rothForm.amount}
                  onChange={(e) => setRothForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rothForm.isQualified}
                    onChange={(e) => setRothForm(f => ({ ...f, isQualified: e.target.checked }))}
                    className="rounded"
                  />
                  Qualified (tax-free)
                </label>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={rothSaving}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {rothSaving ? 'Saving...' : 'Add Withdrawal'}
                </button>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-gray-600 mb-1">Notes (optional)</label>
              <input
                type="text"
                placeholder="e.g. Home purchase, emergency fund"
                value={rothForm.notes}
                onChange={(e) => setRothForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          </form>

          {(formData.roth_withdrawals || 0) > 0 && (
            <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm">
              <strong>Taxable Roth portion:</strong> {formatCurrency(formData.roth_withdrawals || 0)}
              {(formData.roth_early_withdrawal_penalty || 0) > 0 && (
                <span className="ml-4"><strong>Est. 10% penalty:</strong> {formatCurrency(formData.roth_early_withdrawal_penalty)}</span>
              )}
            </div>
          )}

          {rothList.length === 0 ? (
            <p className="text-gray-500 text-sm">No Roth withdrawals recorded for {taxYear}.</p>
          ) : (
            <div className="space-y-2">
              {rothList.map((w, idx) => (
                <div key={w.id || idx} className="flex justify-between items-center border rounded-lg p-3 bg-gray-50">
                  <div>
                    <div className="font-medium">{formatCurrency(w.amount)}</div>
                    <div className="text-xs text-gray-500">
                      {w.withdrawal_date} · {w.is_qualified ? '✅ Qualified (tax-free)' : '⚠️ Non-qualified (taxable)'}
                      {w.notes && ` · ${w.notes}`}
                    </div>
                  </div>
                  {w.id && (
                    <button
                      type="button"
                      onClick={() => handleDeleteRothWithdrawal(w.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Eye },
    { id: 'income-sources', name: 'Income & Roth', icon: PiggyBank },
    { id: 'filing-info', name: 'Filing Info', icon: User },
    { id: '1040', name: 'Form 1040', icon: FileText },
    { id: 'schedule-e', name: 'Schedule E', icon: Home },
    { id: 'adjustments', name: 'Manual Adjustments', icon: Settings },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'income-sources':
        return renderIncomeSources();
      case 'filing-info':
        return renderFilingInfo();
      case '1040':
        return renderForm1040();
      case 'schedule-e':
        return renderScheduleE();
      case 'adjustments':
        return renderManualAdjustments();
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm text-gray-500">
        <Link to="/dashboard" className="hover:text-gray-700">Dashboard</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 font-medium">Tax Forms</span>
      </nav>
      {/* Header - responsive: stacks on mobile */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:justify-between sm:items-start">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Tax Forms & Calculation</h1>
            <p className="text-sm sm:text-base text-gray-600">Complete your return. Data flows from Income, Expenses, and Properties.</p>
            <Disclaimer variant="tax" />
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Tax Year:</label>
              <select
                value={taxYear}
                onChange={handleTaxYearChange}
                className="border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm min-w-[5rem] focus:ring-2 focus:ring-blue-500"
              >
                {getTaxYearOptions().map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {saveStatus === 'saved' && <span className="text-green-600">• Saved</span>}
              {saveStatus === 'saving' && <span className="text-amber-600">• Saving...</span>}
              {saveStatus === 'unsaved' && isDirty && <span className="text-amber-600">• Unsaved</span>}
            </div>
            {isDirty && (
              <button
                onClick={saveManualAdjustments}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition text-sm"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
            )}
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('accessToken');
                  const res = await axios.get(`/api/tax-forms/export-pdf?taxYear=${taxYear}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Form_1040_${taxYear}_filled.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Form 1040 PDF downloaded');
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Failed to export PDF');
                }
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition text-sm"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation - scrollable on mobile */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Add Data bar - links to answer form questions from elsewhere */}
      <div className="sticky top-0 z-10 bg-blue-50 border-b border-blue-100 py-2 px-3 mb-4 flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
        <span className="font-medium text-blue-800 flex items-center gap-1.5">
          <PlusCircle className="h-4 w-4" />
          Add data elsewhere to answer form questions:
        </span>
        <div className="flex flex-wrap gap-2">
          <Link to={`/expense-tracker?from=tax-forms&tab=${activeTab}`} className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <DollarSign className="h-3.5 w-3.5" />
            Expense Tracker
          </Link>
          <span className="text-blue-300">|</span>
          <Link to={`/income-tracker?from=tax-forms&tab=${activeTab}`} className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <TrendingUp className="h-3.5 w-3.5" />
            Income Tracker
          </Link>
          <span className="text-blue-300">|</span>
          <Link to={`/property-manager?from=tax-forms&tab=${activeTab}`} className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <Building2 className="h-3.5 w-3.5" />
            Property Manager
          </Link>
          <span className="text-blue-300">|</span>
          <Link to={`/property-tax-questionnaire?year=${taxYear}&from=tax-forms&tab=${activeTab}`} className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline">
            <Home className="h-3.5 w-3.5" />
            Property Questionnaire
          </Link>
        </div>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default TaxForms;

